import { useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { userProfileState } from "@/store/user-profile";
import { getUserToken, getRefreshToken } from "@/services/storage-data";
import { refreshAccessToken } from "@/services/refresh-token";
import { setSessionPrintHouseId } from "@/services/auth/session-print-house-id";
import { clearValidateSnapshot } from "@/services/auth/validate-cache";
import {
  getStoredOidcPrintHouses,
  getStoredOidcCognitoSub,
  fetchOidcPrintHouses,
  oidcSelectPrintHouse,
  OidcPrintHouse,
} from "@/services/api-service/auth/oidc-api";
import { redirectToPrintHouse } from "@/utils/print-house-navigation";
import { isSharedLoginDomain } from "@/utils/local-domain";

/**
 * One in-flight request for the whole session. The modern rail mounts the switcher only
 * while expanded, so a per-mount effect would hit the API on every open.
 */
let printHousesRequest: Promise<OidcPrintHouse[]> | null = null;

const loadPrintHousesOnce = (): Promise<OidcPrintHouse[]> => {
  if (!printHousesRequest) {
    printHousesRequest = fetchOidcPrintHouses().catch(() => {
      printHousesRequest = null;
      return [];
    });
  }
  return printHousesRequest;
};

/**
 * Data + switching logic shared by every print-house switcher UI (classic header button,
 * modern sidebar select). Switching rebuilds the session and reloads into the new print house.
 */
export const usePrintHouseSwitcher = () => {
  const userProfile = useRecoilValue(userProfileState);
  const [switchingId, setSwitchingId] = useState<string | null>(null);

  // Seed from the localStorage cache (written by the OIDC callback) so the list shows instantly
  // when available, then refresh from the backend below.
  const [printHouses, setPrintHouses] = useState<OidcPrintHouse[]>(() =>
    getStoredOidcPrintHouses(),
  );

  // Switching is only offered on a shared login host (e.g. gomake.net); a specific print-house
  // domain pins the active print house. Localhost is treated as shared for dev.
  const canSwitchPrintHouse = useMemo(() => {
    if (typeof window === "undefined") return false;
    const hostname = window.location.hostname;
    const isLocalhost =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "::1";
    return isLocalhost || isSharedLoginDomain(hostname);
  }, []);

  // The cache is only populated at the OIDC callback origin. After a `/token-auth` handoff or when
  // running locally against a remote backend, it's empty — so fetch the memberships once for the
  // session. Later mounts (menu expand, route changes) reuse that result.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!canSwitchPrintHouse || !getUserToken()) return;

    let cancelled = false;
    (async () => {
      const houses = await loadPrintHousesOnce();
      if (!cancelled && houses.length > 0) {
        setPrintHouses(houses);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canSwitchPrintHouse]);

  const currentPrintHouse = useMemo(
    () =>
      printHouses.find((ph) => ph.printHouseId === userProfile?.printHouseId),
    [printHouses, userProfile?.printHouseId],
  );

  const isAvailable =
    !!printHouses && printHouses.length > 1 && canSwitchPrintHouse;

  /** Resolves to true when a redirect was started; false when nothing changed or it failed. */
  const selectPrintHouse = async (
    printHouse: OidcPrintHouse,
  ): Promise<boolean> => {
    if (switchingId) return false;
    if (printHouse.printHouseId === userProfile?.printHouseId) return false;

    setSwitchingId(printHouse.printHouseId);
    const res = await oidcSelectPrintHouse({
      printHouseId: printHouse.printHouseId,
      cognitoSub: getStoredOidcCognitoSub(),
    });
    if (res?.success) {
      const payload = res.data?.data || {};
      const targetPrintHouseId = printHouse.printHouseId;
      setSessionPrintHouseId(targetPrintHouseId);
      clearValidateSnapshot();
      let token = getUserToken();
      try {
        const refreshed = await refreshAccessToken({
          printHouseId: targetPrintHouseId,
        });
        if (refreshed) {
          token = refreshed;
        }
      } catch {
        // Redirect with whatever tokens are stored so the user is not left mid-switch.
      }
      redirectToPrintHouse({
        domain:
          payload.selectedDomain ?? payload.SelectedDomain ?? printHouse.domain,
        token,
        refreshToken: getRefreshToken(),
      });
      return true;
    }
    setSwitchingId(null);
    return false;
  };

  return {
    printHouses,
    currentPrintHouse,
    currentPrintHouseId: userProfile?.printHouseId,
    isAvailable,
    switchingId,
    selectPrintHouse,
  };
};
